<template>
  <BaseInnerLoading v-if="isRetrievingCustomer" />

  <form class="space-y-6" @submit="onSubmit">
    <div class="flex justify-center p-4">
      <IconBeepaidRequestMoney />
    </div>

    <div>
      <FormFieldLabel> Customer </FormFieldLabel>
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
      >
        <template v-if="customerState === 'SELECT'">
          <BaseCard class="space-y-2 p-4">
            <FindExistingCustomer
              @update:model-value="onUpdateExistingCustomer"
            />
            <ButtonAdd
              class="w-full"
              @click="onClickNewCustomer"
            >
              New Customer
            </ButtonAdd>
          </BaseCard>
        </template>

        <template
          v-else-if="
            customerState === 'EXISTING' && values.customer
          "
        >
          <BaseCard
            class="relative flex flex-wrap items-center justify-between"
          >
            <button
              class="absolute right-2 top-2 ml-2"
              type="button"
              @click="onResetCustomer"
            >
              <IconHeroiconsXMark />
            </button>
            <div class="flex-1">
              <p class="text-lg font-semibold">
                {{ values.customer.name }}
              </p>
              <p
                v-if="values.customer.email"
                class="flex items-center text-sm text-background-fg/87"
              >
                <IconHeroiconsEnvelope class="mr-2" />

                {{ values.customer.email }}
              </p>
              <p
                v-if="values.customer.phone"
                class="flex items-center text-sm text-background-fg/87"
              >
                <IconHeroiconsPhone class="mr-2" />
                {{ values.customer.phone }}
              </p>
            </div>
          </BaseCard>
        </template>

        <template v-else>
          <BaseCard class="relative p-4">
            <button
              class="absolute right-2 top-2 ml-2"
              type="button"
              @click="onResetCustomer"
            >
              <IconHeroiconsXMark />
            </button>
            <div class="space-y-6">
              <FormInputText
                name="customer.name"
                label="Name"
              />

              <FormInputText
                name="customer.email"
                label="Email"
              />

              <FormInputText
                name="customer.phone"
                label="Phone"
              />
            </div>
          </BaseCard>
        </template>
      </Transition>
    </div>

    <FormInputText name="description" label="Description" />

    <FormInputPrice name="amountNet" label="Price" />

    <div class="flex items-center">
      <FormToggleSwitch
        name="vatCode"
        :options="vatCodeOptions"
        class="mr-2 flex-1"
      />
    </div>

    <div class="flex items-center justify-between">
      <FormFieldLabel> Total </FormFieldLabel>
      <span
        class="text-2xl font-semibold text-background-fg/70"
      >
        {{
          formatToCurrencyString(
            (values.amountNet ?? 0) *
              (values.vatCode === "T1" ? 1.2 : 1)
          )
        }}
      </span>
    </div>

    <FormSubmitButton
      class="w-full"
      :is-loading="isSubmitting"
    >
      Send Payment Request
    </FormSubmitButton>
  </form>
</template>

<script setup lang="ts">
import * as z from "zod"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"
import type { ListCustomersResponseItem } from "@/services/customers/listCustomers"

const router = useRouter()

const props = defineProps<{
  customerId?: string
}>()

type CustomerState = "SELECT" | "EXISTING" | "NEW"
const customerState = ref<CustomerState>("SELECT")

const onClickNewCustomer = () => {
  customerState.value = "NEW"
}

const onUpdateExistingCustomer = (
  value: ListCustomersResponseItem | undefined
) => {
  if (value) {
    customerState.value = "EXISTING"
    setFieldValue("customer", {
      name: value.name,
      email: value.email,
      phone: value.phone,
    })
  } else {
    onResetCustomer()
  }
}

const onResetCustomer = () => {
  setFieldValue("customer", {
    name: "",
    email: "",
    phone: "",
  })
  customerState.value = "SELECT"
}

const createPaymentRequestFormSchema = z.object({
  customer: z
    .object({
      name: z.string().min(1, "Name must not be empty"),
      email: z.union([
        z.literal(""),
        z
          .string()
          .email("Please enter a valid email address")
          .nullable(),
      ]),
      phone: z.string().nullable(),
    })
    .superRefine((params, ctx) => {
      if (!params.email && !params.phone) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Either email or phone is required",
          path: ["email"],
        })

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Either email or phone is required",
          path: ["phone"],
        })
      }
    }),
  description: z.string(),
  amountNet: z.number().positive(),
  vatCode: z.union([z.literal("T1"), z.literal("T0")]),
})

const {
  handleSubmit,
  values,
  isSubmitting,
  setFieldValue,
  setErrors,
} = useForm({
  validationSchema: toTypedSchema(
    createPaymentRequestFormSchema
  ),
  initialValues: {
    customer: {
      name: "",
      email: "",
      phone: "",
    },
    description: "",
    amountNet: 0,
    vatCode: "T0",
  },
})

const vatCodeOptions = [
  {
    value: "T0",
    label: "No VAT",
  },
  {
    value: "T1",
    label: "VAT (20%)",
  },
]

const onSubmit = handleSubmit(async (params) => {
  try {
    await createPaymentRequest(params)
    toast.success("Payment request sent!")
    router.push({
      name: "paymentsIndex",
    })
  } catch (error) {
    if (error instanceof ApiError) {
      setErrors(error.requestErrors?.body ?? {})
    }
    toast.error("Failed to create payment request")
  }
})

const isRetrievingCustomer = ref(false)
onMounted(async () => {
  if (props.customerId) {
    isRetrievingCustomer.value = true
    customerState.value = "EXISTING"
    try {
      isRetrievingCustomer.value = true
      const { name, email, phone } = await retrieveCustomer(
        props.customerId
      )

      setFieldValue("customer", {
        name,
        email,
        phone,
      })
    } catch {
      toast.error("Failed to retrieve customer")
      router.push({
        name: "customerDetail",
        params: {
          customerId: props.customerId,
        },
      })
    } finally {
      isRetrievingCustomer.value = false
    }
  }
})
</script>
